import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";

import {
  checkSubdomainExists,
  fetchAppTheme,
} from "@/shared/services/themeService";
import { isCancelRequest } from "@/config/AxiosInstance";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import muiTheme, { createMuiTheme } from "@/config/muiTheme";
import { logger } from "../loggerHelper";
import FullScreenLoader from "@/components/loaders/FullScreenLoader";
import { useDispatch, useSelector } from "react-redux";
import { setAppTheme } from "@/shared/store/themeSlice";
import SubDomainNotExists from "@/shared/components/SubDomainNotExists";
import {
  IS_WHITE_LABELED_DOMAIN,
  SITE_URL,
  WHITE_LABELED_DOMAIN_URL,
} from "@/config/appConfig";
import { getSubdomain } from "../utils";

type ThemeRecord = Record<string, any>;
type AppThemeContextValue = {
  getTheme: (_brandId?: string | number) => Promise<void>;
  isLoading: boolean;
  loginData: ThemeRecord | null;
  theme: ThemeRecord;
};
type AppThemeProviderProps = {
  children: ReactNode;
};
type ThemeState = {
  brand?: {
    selectedBrand?: {
      id?: string | number;
    } | null;
  };
  theme?: {
    themeData?: {
      login?: ThemeRecord;
      theme?: ThemeRecord;
    } | null;
  };
};
type Dispatch = (_action: any) => any;

const AppThemeContext = createContext<Partial<AppThemeContextValue>>({});

export default AppThemeContext;

export const AppThemeProvider = ({ children }: AppThemeProviderProps) => {
  const dispatch = useDispatch() as Dispatch;
  const { themeData: themeDataRdx } = useSelector(
    (state: ThemeState) => state.theme || {}
  );
  const selectedBrandId = useSelector(
    (state: ThemeState) => state.brand?.selectedBrand?.id
  );
  const [theme, setTheme] = useState(() => createMuiTheme(themeDataRdx?.theme));
  const [loginData, setLoginData] = useState<ThemeRecord | null>(
    themeDataRdx?.login || null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isSubDomainExists, setIsSubDomainExists] = useState(true);
  const requestSequence = useRef(0);
  const loadingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousBrand = useRef(selectedBrandId);
  useEffect(() => {
    setTheme(createMuiTheme(themeDataRdx?.theme));
    setLoginData(themeDataRdx?.login || null);
  }, [themeDataRdx]);
  useEffect(
    () => () => {
      requestSequence.current += 1;
      if (loadingTimer.current) clearTimeout(loadingTimer.current);
    },
    []
  );

  const getTheme = useCallback(
    async (brandId?: string | number) => {
      const request = ++requestSequence.current;
      if (loadingTimer.current) clearTimeout(loadingTimer.current);
      try {
        setIsLoading(true);

        const [resSubdomain, resTheme] = await Promise.all([
          checkSubdomainExists(),
          fetchAppTheme({ fk_brand_id: brandId }),
        ]);

        if (request !== requestSequence.current) return;

        if (resSubdomain.status === 200) {
          const currentSubdomain = getSubdomain();
          const isSubdomainMatch = resSubdomain?.data?.is_subdomain_match;
          const isMicroSiteMatch = resSubdomain?.data?.is_microsite_match;

          if (currentSubdomain) {
            // check for sub-domain only
            if (IS_WHITE_LABELED_DOMAIN) {
              if (!isSubdomainMatch) {
                window.location.href = WHITE_LABELED_DOMAIN_URL;
              }
            } else {
              if (!isMicroSiteMatch) {
                window.location.href = SITE_URL;
              }
            }
          } else {
            // if there is no sub-domain, then always true
            setIsSubDomainExists(true);
          }
        }

        if (resTheme.status === 200) {
          const themeData = resTheme.data as {
            login?: ThemeRecord;
            theme?: ThemeRecord;
          };

          setTheme(createMuiTheme(themeData?.theme));
          setLoginData(themeData?.login || null);
          dispatch(setAppTheme(themeData));
        } else {
          throw new Error("Error fetching theme");
        }
      } catch (error) {
        if (request !== requestSequence.current || isCancelRequest(error))
          return;
        logger.error("Error fetching theme:", error);
      } finally {
        if (request === requestSequence.current) {
          loadingTimer.current = setTimeout(() => {
            if (request === requestSequence.current) setIsLoading(false);
          }, 1000);
        }
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (previousBrand.current !== selectedBrandId) {
      setTheme(muiTheme);
      setLoginData(null);
      previousBrand.current = selectedBrandId;
    }
    getTheme(selectedBrandId || "");
  }, [getTheme, selectedBrandId]);

  const isLoginPage =
    window.location.pathname === "/" || window.location.pathname === "/login";

  const contextValue = useMemo<AppThemeContextValue>(
    () => ({ theme, isLoading, loginData, getTheme }),
    [theme, isLoading, loginData, getTheme]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <AppThemeContext.Provider value={contextValue}>
          {isLoginPage ? (
            <>
              {isLoading ? (
                <FullScreenLoader
                  isActive={isLoading}
                  message="Loading..."
                  whiteBg
                />
              ) : isSubDomainExists ? (
                children
              ) : (
                <SubDomainNotExists />
              )}
            </>
          ) : (
            <>
              {isLoading ? (
                <FullScreenLoader
                  isActive={isLoading}
                  // message="Loading..."
                  whiteBg
                  hideIcon
                />
              ) : null}
              {isSubDomainExists ? children : <SubDomainNotExists />}
            </>
          )}
        </AppThemeContext.Provider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export const useAppTheme = () => {
  return useContext(AppThemeContext);
};
